import { NgClass } from '@angular/common';
import { Component, computed, effect, forwardRef, HostBinding, inject, Injector, input, signal } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoService } from '@jsverse/transloco';
import { IconComponent } from '../icons/icon/icon.component';
import { FormFieldAddonComponent } from './components/form-field-addon/form-field-addon.component';
import { FormFieldHintComponent } from './components/form-field-hint/form-field-hint.component';
import { FormFieldLabelComponent } from './components/form-field-label/form-field-label.component';
import { FormFieldAddon, FormFieldError, FormFieldHint, FormFieldLabelAddons } from './form-field.interface';

// Standard Material ErrorStateMatcher - shows errors when invalid AND (touched OR dirty)
class StandardErrorStateMatcher implements ErrorStateMatcher {
  constructor(private component: FormFieldComponent) {}

  isErrorState(): boolean {
    const hasErrors = this.component.hasVisibleErrors();
    const isTouched = this.component.isTouched();
    const isDirty = this.component.isDirty();

    return hasErrors && (isTouched || isDirty);
  }
}

@Component({
  selector: 'ui-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    IconComponent,
    FormFieldAddonComponent,
    FormFieldHintComponent,
    FormFieldLabelComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormFieldComponent),
      multi: true,
    },
  ],
})
export class FormFieldComponent implements ControlValueAccessor {
  private readonly injector = inject(Injector);
  private readonly translocoService = this.getTranslocoService();

  // Component inputs
  readonly label = input<string>();
  readonly labelAddons = input<FormFieldLabelAddons>();
  readonly placeholder = input<string>();
  readonly required = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly fieldType = input<'text' | 'email' | 'password' | 'tel' | 'number' | 'search' | 'url'>('text');
  readonly autocomplete = input<string>('');
  readonly prefix = input<FormFieldAddon>();
  readonly suffix = input<FormFieldAddon>();
  readonly innerPrefix = input<FormFieldAddon>();
  readonly innerSuffix = input<FormFieldAddon>();
  readonly hint = input<FormFieldHint>();
  readonly hintInfo = input<string>();
  readonly rightHint = input<FormFieldHint>();
  readonly rightHintInfo = input<string>();
  readonly translate = input<boolean>(false);
  readonly errors = input<Record<string, FormFieldError>>({});
  readonly fullWidth = input<boolean>(false);
  readonly layout = input<'vertical' | 'horizontal'>('vertical');
  readonly formControl = input<FormControl | null>(null);

  // Internal state
  private readonly _value = signal<string>('');
  private readonly _touched = signal<boolean>(false);
  private readonly _dirty = signal<boolean>(false);
  private readonly _initialValue = signal<string>('');

  // Public readonly signals
  readonly value = this._value.asReadonly();
  readonly touched = this._touched.asReadonly();
  readonly dirty = this._dirty.asReadonly();

  // Create standard error state matcher
  readonly errorStateMatcher = new StandardErrorStateMatcher(this);

  // Create a FormControl for error state management
  readonly internalFormControl = new FormControl('');

  // Use external FormControl if provided, otherwise use internal one
  readonly activeFormControl = computed(() => {
    const externalControl = this.formControl();
    return externalControl || this.internalFormControl;
  });

  // Public methods for ErrorStateMatcher
  isTouched(): boolean {
    return this._touched();
  }

  isDirty(): boolean {
    return this._dirty();
  }

  // HostBinding
  @HostBinding('class') get class() {
    return this.layout() === 'vertical' ? '' : 'horizontal';
  }

  readonly displayPlaceholder = computed(() => {
    const placeholderText = this.placeholder();
    return this.translate() && placeholderText && this.translocoService
      ? this.translocoService.translate(placeholderText)
      : placeholderText;
  });

  readonly visibleErrors = computed(() => {
    const errors = this.errors();

    if (!errors || Object.keys(errors).length === 0) {
      return [];
    }

    const result: string[] = [];

    Object.values(errors).forEach((errorConfig) => {
      // Only show errors when explicitly set to true
      if (errorConfig.show) {
        const message =
          this.translate() && this.translocoService
            ? this.translocoService.translate(errorConfig.message)
            : errorConfig.message;
        result.push(message);
      }
    });

    return result;
  });

  readonly hasVisibleErrors = computed(() => this.visibleErrors().length > 0);

  // Update FormControl error state based on our logic
  readonly updateFormControlState = computed(() => {
    const shouldShowErrors = this.hasVisibleErrors() && (this.isTouched() || this.isDirty());
    const activeControl = this.activeFormControl();

    // Only update internal FormControl if we're using it (no external FormControl provided)
    if (activeControl === this.internalFormControl) {
      // Update value
      if (this.internalFormControl.value !== this.value()) {
        this.internalFormControl.setValue(this.value(), { emitEvent: false });
      }

      // Update disabled state
      if (this.disabled()) {
        if (this.internalFormControl.enabled) {
          this.internalFormControl.disable({ emitEvent: false });
        }
      } else {
        if (this.internalFormControl.disabled) {
          this.internalFormControl.enable({ emitEvent: false });
        }
      }

      // Update error state
      if (shouldShowErrors) {
        // Set errors on the FormControl to trigger Material's error state
        const errors: Record<string, boolean> = {};
        this.visibleErrors().forEach((_, index) => {
          errors[`error_${index}`] = true;
        });
        this.internalFormControl.setErrors(errors);
        this.internalFormControl.markAsTouched();
      } else {
        this.internalFormControl.setErrors(null);
      }
    }

    return shouldShowErrors;
  });

  readonly fullWidthClass = computed(() => (this.fullWidth() ? 'w-full' : ''));

  constructor() {
    // Subscribe to state changes to trigger FormControl updates
    effect(() => {
      this.updateFormControlState();
    });
  }

  // ControlValueAccessor implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  private onChange = (_value: string): void => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched = (): void => {};

  writeValue(value: string): void {
    const newValue = value || '';
    this._value.set(newValue);
    this._initialValue.set(newValue);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  setDisabledState(_isDisabled: boolean): void {}

  // Event handlers
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this._value.set(value);
    this._dirty.set(value !== this._initialValue());
    this.onChange(value);
  }

  onInputBlur(): void {
    this._touched.set(true);
    this.onTouched();
  }

  // Safe method to get TranslocoService
  private getTranslocoService(): TranslocoService | null {
    try {
      return this.injector.get(TranslocoService, null);
    } catch {
      return null;
    }
  }
}
